import { useEffect, useState } from "react";
import {
  getNotifications,
  markNotificationRead,
  deleteNotification,
} from "../../api/notificationApi";
import Loader from "../../components/Loader";

const NotificationPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const res = await getNotifications();
      setNotifications(res.data);
    } catch (err) {
      console.error("Failed to load notifications:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const handleRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
    } catch (err) {
      console.error("Failed to mark read:", err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      console.error("Failed to delete notification:", err);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* HEADER */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Notifications</h2>
          <p className="text-sm text-gray-500">
            View and manage your in-app notifications
          </p>
        </div>
        <button
          onClick={loadNotifications}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition font-medium text-sm"
        >
          Refresh
        </button>
      </div>

      {/* NOTIFICATION LIST */}
      <div className="bg-white rounded-xl shadow border overflow-hidden">
        {loading ? (
          <div className="text-center py-8">
            <Loader />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg font-medium">No notifications yet</p>
            <p className="text-sm">You are all caught up!</p>
          </div>
        ) : (
          <div className="divide-y">
            {notifications.map((n) => (
              <div
                key={n.id}
                className={`flex justify-between items-start gap-4 px-6 py-4 transition hover:bg-gray-50 ${
                  !n.is_read ? "bg-blue-50/40" : ""
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-800 text-base">
                      {n.title}
                    </h3>
                    {!n.is_read && (
                      <span className="w-2.5 h-2.5 bg-blue-600 rounded-full" title="Unread" />
                    )}
                    {n.type && n.type !== "GENERAL" && (
                      <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-indigo-100 text-indigo-800 uppercase">
                        {n.type.replace("_", " ")}
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm mb-2 whitespace-pre-line">
                    {n.message}
                  </p>
                  <p className="text-xs text-gray-400">
                    {new Date(n.created_at).toLocaleString()}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  {!n.is_read && (
                    <button
                      onClick={() => handleRead(n.id)}
                      className="px-3 py-1 bg-white border rounded text-xs font-semibold text-blue-600 hover:bg-blue-50 transition"
                    >
                      Mark read
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(n.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 transition rounded-full hover:bg-gray-100"
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationPage;
